import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { produce } from 'immer';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useState } from 'react';

import { useUser } from '@sophocles/app';
import SearchField from '@sophocles/components/SearchField';
import { deriveDefaultValue, reduceByKeyWithDefault } from '@sophocles/utils/table';

const GROUP_KEY = 'group';
const DEFAULT_GROUP = 'Other';

function renderFilterItem(group, filter, index, onChange) {
  const { id, value, label, type, options } = filter;
  switch (type) {
    case 'bool':
      return (
        <FormControlLabel
          label={label}
          control={
            <Checkbox name={id} size="small" checked={value} onChange={e => onChange(group, index, e.target.checked)} />
          }
        />
      );
    case 'string':
      return (
        <TextField
          fullWidth
          name={id}
          label={label}
          size="small"
          type="text"
          value={value}
          onChange={e => onChange(group, index, e.target.value)}
        />
      );
    case 'number':
      return (
        <TextField
          fullWidth
          name={id}
          label={label}
          size="small"
          type="number"
          value={value}
          onChange={e => onChange(group, index, e.target.value)}
        />
      );
    case 'select':
      if (!options) {
        return null;
      }
      return (
        <FormControl fullWidth size="small">
          <InputLabel id={`filter-select-${id}-label`}>{label}</InputLabel>
          <Select
            labelId={`filter-select-${id}-label`}
            id={`filter-select-${id}`}
            value={value}
            label={label}
            onChange={e => onChange(group, index, e.target.value)}
          >
            {options.map((opt, index) => (
              <MenuItem key={`filter-${id}-${index}`} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    default:
      return null;
  }
}

const EnhancedTableToolbar = ({
  title,
  editable = false,
  searchable = false,
  deletable = false,
  dateRange = false,
  selected = [],
  filters = [],
  actions = [],
  search = '',
  startDate,
  endDate,
  allowsRefresh,
  onRefreshClick,
  onDateRangeChange,
  onApplyFilters,
  onClearFilters,
  iconButton = null,
  onSearch,
  onDelete,
}) => {
  const [filterMenuElement, setFilterMenuElement] = useState(null);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [filtersByGroup, setFiltersByCategory] = useState({});
  const [start, setStart] = useState(startDate);
  const [end, setEnd] = useState(endDate);
  const [searchText, setSearchText] = useState(search);

  const numSelected = selected.length;

  const { user } = useUser();
  const dateFormat = user.getIn(['options', 'date_format'], 'yyyy-MM-dd');

  useEffect(() => {
    setFiltersByCategory(
      reduceByKeyWithDefault(filters, GROUP_KEY, DEFAULT_GROUP, false, [
        { key: 'value', value: deriveDefaultValue },
        { key: 'defaultValue', value: deriveDefaultValue },
        { key: 'touched', value: false },
      ]),
    );
  }, [filters]);

  const setFilterValue = (group, index, value) => {
    const isDifferent = value !== filtersByGroup[group][index].defaultValue;

    setFiltersByCategory(
      produce(filtersByGroup, draft => {
        draft[group][index].value = value;
        draft[group][index].touched = isDifferent;
      }),
    );

    return filtersByGroup[group][index].index;
  };

  const handleSearchChange = value => {
    setSearchText(value);
  };

  const handleSearch = newValue => {
    setSearchText(newValue);
    if (onSearch) {
      onSearch(newValue);
    }
  };

  const handleSearchKeyDown = e => {
    if (e.keyCode === 13) {
      onSearch(searchText);
    }
  };

  const handleStartChange = value => {
    setStart(value);
  };

  const handleEndChange = value => {
    setEnd(value);
  };

  const handleFilterButtonClick = useCallback(event => {
    setFilterMenuElement(event.currentTarget);
    setIsFilterMenuOpen(true);
  }, []);

  const handleFilterMenuClose = useCallback(() => {
    setIsFilterMenuOpen(false);
    setFilterMenuElement(null);
  }, []);

  const handleFilterChange = useCallback(
    (group, index, value) => {
      return setFilterValue(group, index, value);
    },
    [filtersByGroup],
  );

  const handleApplyFilters = useCallback(() => {
    const changes = [];

    Object.keys(filtersByGroup).forEach(key => {
      filtersByGroup[key].forEach(filter => {
        if (filter.onChange) {
          filter.onChange(filter.value);
        }
        changes.push({ index: filter.index, value: filter.value });
      });
    });

    if (onApplyFilters) {
      onApplyFilters(changes);
    }

    handleFilterMenuClose();
  }, [filtersByGroup]);

  const handleClearFilters = useCallback(() => {
    setFiltersByCategory(
      reduceByKeyWithDefault(filters, GROUP_KEY, DEFAULT_GROUP, false, [{ key: 'value', value: deriveDefaultValue }]),
    );

    if (onClearFilters) {
      onClearFilters();
    } else if (onApplyFilters) {
      onApplyFilters([]);
    }

    handleFilterMenuClose();
  }, [filters]);

  useEffect(() => {
    if (dateRange && onDateRangeChange) {
      onDateRangeChange(start, end);
    }
  }, [start, end]);

  return (
    <Toolbar
      sx={{
        py: 1,
        px: 2,
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Grid container rowSpacing={1} columnSpacing={0} sx={{ alignItems: 'center' }}>
        <Grid
          size={{ lg: 6, md: 6, sm: 6, xs: 12 }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { sm: 'start', xs: 'center' },
            textAlign: { sm: 'left', xs: 'center' },
            mb: { sm: 0, xs: 1 },
          }}
        >
          <span style={{ marginRight: '8px' }}>
            {numSelected > 1 ? (
              <Typography variant="h6">{numSelected} selected</Typography>
            ) : (
              title && (
                <Typography variant="h6" sx={{ mt: 0 }}>
                  {title}
                </Typography>
              )
            )}
          </span>
          {allowsRefresh && onRefreshClick && (
            <Tooltip title="Refresh">
              <IconButton
                onClick={onRefreshClick}
                sx={{
                  mr: 1,
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
          {iconButton && iconButton}
        </Grid>

        {searchable && (
          <Grid size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <SearchField
              fullWidth
              size="small"
              value={searchText}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onSearch={handleSearch}
              sx={{ m: 0 }}
            />
          </Grid>
        )}

        {actions != null && <Grid size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>{actions}</Grid>}

        {dateRange && (
          <Grid
            size={{ lg: 6, md: 12, sm: 12, xs: 12 }}
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <DatePicker
              fullWidth
              label="Start"
              size="small"
              format={dateFormat}
              value={start}
              onChange={newDate => handleStartChange(newDate)}
              onAccept={newDate => handleStartChange(newDate)}
              slotProps={{ textField: { size: 'small' } }}
              sx={{ width: '100%', mr: 1 }}
            />
            <DatePicker
              fullWidth
              label="End"
              size="small"
              format={dateFormat}
              value={end}
              onChange={newDate => handleEndChange(newDate)}
              onAccept={newDate => handleEndChange(newDate)}
              slotProps={{ textField: { size: 'small' } }}
              sx={{ width: '100%' }}
            />
          </Grid>
        )}

        {numSelected > 0 && onDelete != null && deletable ? (
          <Grid>
            <Tooltip title="Delete">
              <IconButton
                onClick={() => onDelete(selected)}
                sx={{
                  mr: 1,
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        ) : (
          filters.filter(f => f.visible ?? true).length > 0 && (
            <Grid>
              <Tooltip title="Filter">
                <IconButton
                  onClick={handleFilterButtonClick}
                  sx={{
                    mr: 1,
                  }}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
              <Popover
                sx={{
                  '& .MuiPaper-root': {
                    maxWidth: '480px',
                    px: 3,
                    py: 2,
                    '& h6': { mb: 2 },
                    '& .MuiGrid-container': { mb: 1 },
                  },
                }}
                open={isFilterMenuOpen}
                anchorEl={filterMenuElement}
                onClose={handleFilterMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0 }}>
                  Filters
                </Typography>
                {Object.keys(filtersByGroup).map((group, index) => {
                  if (filtersByGroup[group].filter(g => g.visible ?? true).length === 0) {
                    return null;
                  }
                  const filtersByType = reduceByKeyWithDefault(filtersByGroup[group], 'type', 'unknown', false, [
                    { key: 'groupIndex', value: 'index' },
                  ]);
                  return (
                    <Fragment key={`${group}-${index}`}>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                        {group}
                      </Typography>
                      <Grid container>
                        {Object.keys(filtersByType).map(typeKey =>
                          filtersByType[typeKey]
                            .filter(f => f.visible ?? true)
                            .map(filter => (
                              <Grid
                                key={`${group}-${index}-${typeKey}-${filter.id}`}
                                size={typeKey == 'bool' ? 6 : 12}
                                sx={{ p: 1 }}
                              >
                                {renderFilterItem(group, filter, filter.groupIndex, handleFilterChange)}
                              </Grid>
                            )),
                        )}
                      </Grid>
                    </Fragment>
                  );
                })}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Button onClick={() => handleClearFilters()}>Clear Filters</Button>
                  <Button color="success" onClick={() => handleApplyFilters()}>
                    Apply
                  </Button>
                </Box>
              </Popover>
            </Grid>
          )
        )}
      </Grid>
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  title: PropTypes.string,
  editable: PropTypes.bool,
  searchable: PropTypes.bool,
  dateRange: PropTypes.bool,
  deletable: PropTypes.bool,
  selected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  filters: PropTypes.arrayOf(PropTypes.object),
  actions: PropTypes.arrayOf(PropTypes.element),
  search: PropTypes.string,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  allowsRefresh: PropTypes.bool,
  onRefreshClick: PropTypes.func,
  onDateRangeChange: PropTypes.func,
  onApplyFilters: PropTypes.func,
  onClearFilters: PropTypes.func,
  onSearch: PropTypes.func,
  onDelete: PropTypes.func,
};

export default EnhancedTableToolbar;
